import { cn } from '../../lib/utils';

export const AnalyticsChart = ({ 
    data = [], 
    type = 'bar',
    height = 200,
    className 
}) => {
    const maxValue = Math.max(...data.map(d => d.value), 1);
    
    if (type === 'bar') {
        return (
            <div className={cn('flex items-end justify-between gap-2', className)} style={{ height }}>
                {data.map((item, i) => (
                    <div 
                        key={i}
                        className="flex flex-col items-center flex-1 gap-1"
                    >
                        <div 
                            className="w-full bg-primary/80 rounded-t transition-all duration-500 hover:bg-primary"
                            style={{ 
                                height: `${(item.value / maxValue) * (height - 24)}px`,
                                minHeight: item.value > 0 ? 4 : 0
                            }}
                        />
                        <span className="text-xs text-muted-foreground truncate w-full text-center">
                            {item.label}
                        </span>
                    </div>
                ))}
            </div>
        );
    }
    
    if (type === 'line') {
        const points = data.map((d, i) => ({
            x: (i / (data.length - 1)) * 100,
            y: 100 - ((d.value / maxValue) * 80)
        }));
        
        const pathD = points.length > 0 
            ? `M ${points.map(p => `${p.x} ${p.y}`).join(' L ')}`
            : '';
        
        return (
            <div className={cn('relative', className)} style={{ height }}>
                <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
                    <defs>
                        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3"/>
                            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0"/>
                        </linearGradient>
                    </defs>
                    
                    {/* Grid lines */}
                    {[20, 40, 60, 80].map(y => (
                        <line 
                            key={y}
                            x1="0" y1={y} x2="100" y2={y}
                            stroke="currentColor"
                            strokeOpacity="0.1"
                            strokeWidth="0.5"
                        />
                    ))}
                    
                    {/* Area fill */}
                    {points.length > 1 && (
                        <path
                            d={`${pathD} L 100 100 L 0 100 Z`}
                            fill="url(#lineGradient)"
                        />
                    )}
                    
                    {/* Line */}
                    <path
                        d={pathD}
                        fill="none"
                        stroke="hsl(var(--primary))"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                    />
                    
                    {/* Points */}
                    {points.map((p, i) => (
                        <circle 
                            key={i}
                            cx={p.x} 
                            cy={p.y} 
                            r="3"
                            fill="hsl(var(--background))"
                            stroke="hsl(var(--primary))"
                            strokeWidth="2"
                            vectorEffect="non-scaling-stroke"
                        />
                    ))}
                </svg>
                
                {/* Labels */}
                <div className="flex justify-between mt-2">
                    {data.map((item, i) => (
                        <span key={i} className="text-xs text-muted-foreground">
                            {item.label}
                        </span>
                    ))}
                </div>
            </div>
        );
    }
    
    if (type === 'donut') {
        const total = data.reduce((sum, d) => sum + d.value, 0);
        let currentAngle = -90;
        
        const segments = data.map((item, i) => {
            const angle = (item.value / total) * 360;
            const startAngle = currentAngle;
            currentAngle += angle;
            
            const startRad = (startAngle * Math.PI) / 180;
            const endRad = ((startAngle + angle) * Math.PI) / 180;
            
            const x1 = 50 + 40 * Math.cos(startRad);
            const y1 = 50 + 40 * Math.sin(startRad);
            const x2 = 50 + 40 * Math.cos(endRad);
            const y2 = 50 + 40 * Math.sin(endRad);
            
            const largeArc = angle > 180 ? 1 : 0;
            
            return {
                path: `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`,
                color: item.color || `hsl(${(i * 90) % 360}, 70%, 50%)`,
                label: item.label,
                value: item.value,
                percentage: Math.round((item.value / total) * 100)
            };
        });
        
        return (
            <div className={cn('flex items-center gap-6', className)}>
                <div className="relative" style={{ width: height, height }}>
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                        {segments.map((seg, i) => (
                            <path
                                key={i}
                                d={seg.path}
                                fill={seg.color}
                                className="opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
                            />
                        ))}
                        <circle cx="50" cy="50" r="25" fill="hsl(var(--background))" />
                        <text 
                            x="50" 
                            y="50" 
                            textAnchor="middle" 
                            dominantBaseline="middle"
                            className="fill-foreground text-lg font-bold"
                        >
                            {total}
                        </text>
                        <text 
                            x="50" 
                            y="60" 
                            textAnchor="middle" 
                            dominantBaseline="middle"
                            className="fill-muted-foreground text-xs"
                        >
                            Total
                        </text>
                    </svg>
                </div>
                <div className="flex flex-col gap-2">
                    {segments.map((seg, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <div 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: seg.color }}
                            />
                            <span className="text-sm">{seg.label}</span>
                            <span className="text-sm text-muted-foreground">
                                ({seg.percentage}%)
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    
    return null;
};

export const StatCard = ({ 
    title, 
    value, 
    change, 
    changeType = 'neutral',
    icon: Icon,
    className 
}) => {
    const changeColors = {
        positive: 'text-emerald-500',
        negative: 'text-red-500',
        neutral: 'text-muted-foreground'
    };
    
    return (
        <div className={cn(
            'p-4 rounded-xl bg-muted/50 hover:bg-muted/70 transition-colors',
            className
        )}>
            <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">{title}</span>
                {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
            </div>
            <div className="flex items-end gap-2">
                <span className="text-2xl font-bold">{value}</span>
                {change && (
                    <span className={cn('text-sm', changeColors[changeType])}>
                        {changeType === 'positive' && '+'}
                        {change}
                    </span>
                )}
            </div>
        </div>
    );
};

export const ProgressRing = ({ 
    value, 
    maxValue = 100, 
    size = 80, 
    strokeWidth = 8,
    label,
    className 
}) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const progress = Math.min(value / maxValue, 1);
    const offset = circumference - (progress * circumference);
    
    return (
        <div className={cn('relative inline-flex items-center justify-center', className)}>
            <svg width={size} height={size} className="transform -rotate-90">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="hsl(var(--muted))"
                    strokeWidth={strokeWidth}
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="hsl(var(--primary))"
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-lg font-bold">{Math.round(value)}</span>
                {label && <span className="text-xs text-muted-foreground">{label}</span>}
            </div>
        </div>
    );
};
